import { webStorage } from 'sidecar-utilities'

import { openAccessibilityControls } from 'components/_common/AccessibeTrigger'

/**
 * These are the items that we use for the app navigation.
 *
 * @param {function} handleLogout - The function to call when the user clicks
 *   the logout button.
 * @param {object} accountMetadata - The account metadata object.
 * @param {object} policy - The policy object.
 * @param {string} selectedMemberUuid - The UUID of the currently selected
 *   member from Redux state.
 * @returns {Array} An array of navigation items.
 */
export const getGlobalNavItems = ({
  handleLogout,
  accountMetadata = {},
  policy = {},
  selectedMemberUuid,
  canViewExpenses = false,
}) => {
  // some of our menu items are meant only for specific products.
  const isEB = accountMetadata.productTypes?.includes('FIXED_INDEMNITY')
  const isACA = accountMetadata.productTypes?.includes('ACA')

  // Check if the member is under Koch ASO underwriter
  // Koch ASO members should not see Paytient financing (TMT-2541)
  const isKochASO = policy?.underwriter === 'Koch ASO'

  // admin users who are impersonating members should not be able to access everything
  const isAdmin = webStorage.getItem('isAdmin')

  // if you re-arrange these, make sure that you update which ones can
  // be shown when an admin is impersonating a member. See below.
  let globalNavItems = [
    {
      mobileIcon: 'sidecar',
      title: 'Dashboard',
      // Include the selected member UUID in the path if available
      // This preserves the member selection when navigating back from other pages
      path: selectedMemberUuid
        ? `/dashboard/${selectedMemberUuid}`
        : '/dashboard',
      pathType: 'internal',
      selectedPaths: ['/dashboard'],
    },
  ]

  // We no longer need to support ACA members accessing care search.
  if (!isACA) {
    // selectedPaths keeps the highlight active on /find-care so the indicator
    // doesn't disappear once a member crosses into member-app.
    globalNavItems.push({
      mobileIcon: 'search',
      title: 'Find care',
      path: '/care',
      pathType: 'internal',
      selectedPaths: ['/care', '/find-care'],
    })
  }

  if (isAdmin) {
    if (canViewExpenses) {
      globalNavItems.push({
        mobileIcon: 'receipt',
        title: 'Expenses',
        path: '/expenses',
        pathType: 'internal',
        selectedPaths: ['/expenses'],
      })
    }
    globalNavItems.push({
      mobileIcon: 'question',
      title: 'User guides',
      path: '/help',
      pathType: 'internal',
      selectedPaths: ['/help'],
    })
  } else {
    globalNavItems.push(
      {
        mobileIcon: 'receipt',
        title: 'Expenses',
        path: '/expenses',
        pathType: 'internal',
        selectedPaths: ['/expenses'],
      },
      // mobile link that navigates to the account menu PAGE
      {
        mobileIcon: 'customer',
        title: 'Account',
        path: '/accountMenu',
        pathType: 'internal',
        selectedPaths: ['/account', '/accountMenu', '/cards'],
        className: 'md:hidden',
      },
      // desktop link that opens the account dropdown
      {
        mobileIcon: 'customer',
        title: 'Account',
        selectedPaths: ['/account', '/accountMenu', '/cards'],
        className: 'max-md:hidden',
      },
    )
  }

  // --- Account menu items ---
  // We only need these if an admin is not impersonating a member
  const accountItemsTop = [
    {
      title: 'Coverage summary',
      subtitle: 'Balances, policy limits, etc',
      icon: 'verify',
      path: '/account/coverage',
      pathType: 'internal',
    },
    {
      title: 'Billing',
      subtitle: 'Autopayments, payment methods, and billing history',
      icon: 'money',
      path: '/account/billing',
      pathType: 'internal',
    },
    {
      title: 'Visa card(s)',
      subtitle: 'Card statuses and balances',
      icon: 'creditCard',
      path: '/account/cards',
      pathType: 'internal',
    },
  ]

  const accountItemsMiddle = [
    {
      title: 'Profile',
      subtitle: 'Contact info and password',
      icon: 'customer',
      path: '/account/profile',
      pathType: 'internal',
    },
    {
      title: 'Accessibility controls',
      subtitle: 'Adjust the app to your needs',
      icon: 'accessibility',
      // The attribute Accessibe binds to can't live on this item — it only
      // exists while the menu is open. AccessibeTrigger has the details.
      onClick: openAccessibilityControls,
    },
    {
      title: 'Documents',
      subtitle: 'Medical records and other confidential files',
      icon: 'document',
      path: '/account/documents',
      pathType: 'internal',
    },
    {
      title: 'User guides',
      subtitle: 'Read answers to common questions',
      icon: 'question',
      path: '/help',
      pathType: 'internal',
    },
    {
      title: 'Forms',
      subtitle: 'Download auth, appeal, or enrollment forms',
      icon: 'document',
      path: 'https://sidecarhealth.com/forms',
      pathType: 'external',
    },
  ]

  const accountItemsBottom = [
    {
      title: 'Sign out',
      onClick: e => {
        e.preventDefault()
        handleLogout()
      },
    },
  ]

  // Group products have financing available.
  // Koch ASO members should not see Paytient financing (TMT-2541)
  if (!isEB && !isKochASO) {
    accountItemsTop.push({
      title: 'Financing',
      subtitle: 'Convenient, custom payment plans without interest or fees',
      icon: 'bank',
      path: '/account/finance',
      pathType: 'internal',
    })
  }

  // Adds all the acount menu items into the account menu. They're
  // arranged by section so that's why they're split like this. See
  // the Menu component for more info.
  if (!isAdmin) {
    globalNavItems[globalNavItems.length - 1].menuItems = [
      {
        items: [...accountItemsTop],
      },
      {
        items: [...accountItemsMiddle],
      },
      {
        items: [...accountItemsBottom],
      },
    ]
  }

  return globalNavItems
}
